import type { ClientPositionView } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { normalizeProgramSurfaceType } from "@/hooks/positionDisplayAmount";
import { useClientContextKey } from "@/hooks/useClientContextKey";
import { usePositionBalances } from "@/hooks/usePositionBalances";
import { fetchClientProgramSurfaces } from "@/services/clientPositions";

import { groupHoldings, type Holding } from "./holdings";

/**
 * The client's positions as holdings (Programs abstracted away), with live
 * balances resolved and Program Surface types read from the catalog when the
 * positions payload omits them. Shares query keys with `usePortfolioTotals`,
 * so rows and totals read the same cached balances.
 */
export function useHoldings(positions: ClientPositionView[]): Holding[] {
    const balances = usePositionBalances(positions);
    const needsCatalog = positions.some((p) => normalizeProgramSurfaceType(p.programSurface.type) === null);
    const contextKey = useClientContextKey();
    const { data: programSurfaces } = useQuery({
        queryKey: ["client-program-surfaces", contextKey],
        queryFn: fetchClientProgramSurfaces,
        enabled: needsCatalog,
        staleTime: 5 * 60 * 1000
    });
    // `usePositionBalances` returns a fresh Map each render; key the memo on
    // its contents so rows don't regroup on every render.
    const balanceSignature = [...balances.entries()]
        .map(([uuid, b]) => `${uuid}:${b.isLoading ? "…" : (b.amount?.toString() ?? "∅")}:${b.shouldFetchLiveUsdv}`)
        .join("|");
    return useMemo(
        () =>
            groupHoldings(positions, {
                balances,
                resolvedTypeOf: (p) => programSurfaces?.find((s) => s.uuid === p.programSurface.uuid)?.type ?? null
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `balanceSignature` stands in for `balances`
        [positions, balanceSignature, programSurfaces]
    );
}
